import { HTTP_HEADERS } from '@api/api';
import { getMe } from '@api/login';
import { instance } from '@api/setup';
import { useDescope, useSession, refresh } from '@descope/react-sdk';
import { APP_ROUTES } from 'constants/routes';
import { useNavigate } from 'hooks';
import { isEmpty } from 'lodash';
import React, { FC, useCallback, useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { IChildrenProps } from 'types/common';
import { IConsoleAuth } from 'types/login';
import { IConsoleUser } from 'types/users';
import { useEffectOnce, useLocalStorage } from 'usehooks-ts';
import LoginLoader from './LoginLoader';

const LOCALSTORAGE_LAST_ROUTE_KEY = 'lastRoute';

let isMeLoadingInternal = false;

export const AuthContext = React.createContext<IConsoleAuth>({
	csrfToken: '',
	authenticated: false,
	consoleUser: {},
	sessionToken: '',
	logout: () => {},
	logoutAll: () => {},
	checkLogin: () => {}
} as any as IConsoleAuth);

export const RequireAuth: FC<IChildrenProps> = ({ children }) => {
	const auth = useDescope();
	const session = useSession();
	const { pathname, state } = useLocation();
	const navigate = useNavigate();
	const { firstSeen } = (state || {}) as { firstSeen: boolean };

	const [, setLastRoute] = useLocalStorage(LOCALSTORAGE_LAST_ROUTE_KEY, '');
	const [consoleState, setConsoleState] = React.useState<
		| {
				consoleUser: IConsoleUser;
				csrfToken: string;
				isError: boolean;
				isMeLoading: boolean;
		  }
		| Record<string, any>
	>({});

	const checkLogin = useCallback(async () => {
		try {
			if (isMeLoadingInternal) {
				return;
			}
			isMeLoadingInternal = true;
			setConsoleState({
				isMeLoading: true
			});

			const { user, csrfTokenFromHeader } = await getMe();
			instance.defaults.headers.common[HTTP_HEADERS.csrf] =
				csrfTokenFromHeader || '';
			isMeLoadingInternal = false;
			setConsoleState({
				consoleUser: user,
				csrfToken: csrfTokenFromHeader,
				isError: false,
				isMeLoading: false
			});
			// force refresh to update react sdk with changed permissions after project creation.
			if (firstSeen) {
				await refresh();
			}
		} catch (ex) {
			isMeLoadingInternal = false;
			delete instance.defaults.headers.common[HTTP_HEADERS.csrf];
			setConsoleState({ isError: true, isMeLoading: false });
			setLastRoute(pathname);
		}
	}, [setConsoleState, setLastRoute, pathname, firstSeen]);

	useEffectOnce(() => {
		(async () => {
			if (!consoleState.isMeLoading) {
				await checkLogin();
			}
		})();
	});

	const logoutOverride = useCallback(async () => {
		setLastRoute(pathname);
		const res = await auth.logout();
		navigate(APP_ROUTES.login);
		return res;
	}, [auth, setLastRoute, pathname, navigate]);

	const consoleAuth = useMemo(
		() => ({
			authenticated: session.isAuthenticated,
			...auth,
			logout: logoutOverride,
			consoleUser: consoleState.consoleUser || {},
			csrfToken: consoleState.csrfToken || '',
			checkLogin
		}),
		[auth, consoleState, checkLogin, logoutOverride, session.isAuthenticated]
	);

	if (firstSeen && consoleState.isMeLoading) {
		return <LoginLoader />;
	}

	if (consoleState.isError) {
		return <Navigate to={APP_ROUTES.login} replace />;
	}

	if (isEmpty(consoleState.consoleUser)) return null;

	return (
		<AuthContext.Provider value={consoleAuth}>{children}</AuthContext.Provider>
	);
};
export { LOCALSTORAGE_LAST_ROUTE_KEY };

export const clearAuth = () => {
	isMeLoadingInternal = false;
};
